import { useState, useCallback, useEffect } from "react";
import { api, getErrorMessage } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, FileSpreadsheet, Download, Loader2, CheckCircle2, AlertTriangle, XCircle, RotateCcw, Trash2, Undo2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  ok: { icon: CheckCircle2, color: "text-emerald-700", bg: "bg-emerald-50", label: "OK" },
  warning: { icon: AlertTriangle, color: "text-amber-700", bg: "bg-amber-50", label: "Avertissement" },
  error: { icon: XCircle, color: "text-red-700", bg: "bg-red-50", label: "Erreur" },
};

const formatMoney = (v) => {
  if (v === null || v === undefined) return "—";
  try { return `${Number(v).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`; }
  catch { return String(v); }
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");

export const ImportOrdersPage = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [workTypeMap, setWorkTypeMap] = useState({});
  const [sellerMap, setSellerMap] = useState({});
  const [autoCreateClients, setAutoCreateClients] = useState(true);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [batches, setBatches] = useState([]);

  const loadBatches = useCallback(async () => {
    try {
      const r = await api.listOrdersImportBatches();
      setBatches(r.data || []);
    } catch { /* silent */ }
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setWorkTypeMap({});
    setSellerMap({});
    setResult(null);
  };

  const uploadPreview = async () => {
    if (!file) return;
    setUploading(true);
    setPreview(null);
    setResult(null);
    try {
      const r = await api.previewOrdersImport(file);
      setPreview(r.data);
      const wtInit = {};
      (r.data.unknown_work_types || []).forEach((w) => { wtInit[w] = "__create__"; });
      setWorkTypeMap(wtInit);
      const sInit = {};
      (r.data.unknown_sellers || []).forEach((s) => { sInit[s] = "__create__"; });
      setSellerMap(sInit);
    } catch (e) {
      toast.error(getErrorMessage(e, "Fichier invalide"));
    } finally {
      setUploading(false);
    }
  };

  const doImport = async () => {
    if (!preview) return;
    setImporting(true);
    try {
      const r = await api.confirmOrdersImport({
        rows: preview.rows,
        work_type_mapping: workTypeMap,
        seller_mapping: sellerMap,
        auto_create_clients: autoCreateClients,
      });
      setResult(r.data);
      toast.success(`Import réussi : ${r.data.created_orders} commandes`);
      loadBatches();
    } catch (e) {
      toast.error(getErrorMessage(e, "Import échoué"));
    } finally {
      setImporting(false);
    }
  };

  const rollback = async (batchId) => {
    if (!window.confirm("Supprimer toutes les commandes de ce batch ? Cette action est irréversible.")) return;
    try {
      const r = await api.rollbackOrdersImport(batchId);
      toast.success(`Batch annulé (${r.data.deleted.orders} commandes supprimées)`);
      loadBatches();
      if (result?.batch_id === batchId) reset();
    } catch (e) {
      toast.error(getErrorMessage(e, "Rollback échoué"));
    }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("token");
    const resp = await fetch(api.ordersImportTemplateUrl(), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "import_commandes_template.xlsx";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const okCount = preview?.summary?.ok || 0;
  const warnCount = preview?.summary?.warning || 0;
  const errorCount = preview?.summary?.error || 0;
  const importableCount = okCount + warnCount;

  return (
    <div className="space-y-6" data-testid="import-orders-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary/10 text-primary inline-flex items-center justify-center">
          <Upload className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Import de commandes</h1>
          <p className="text-sm text-muted-foreground">
            Importez votre historique de commandes depuis un fichier Excel ou CSV.
          </p>
        </div>
      </div>

      {/* Step 1: upload */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> 1. Télécharger un fichier
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="bg-muted/40 rounded-md p-3 text-sm">
            <p className="font-medium">Colonnes attendues :</p>
            <p className="text-xs text-muted-foreground mt-1">
              numero_commande (optionnel), <strong>client</strong>, ville, <strong>type_travaux</strong>, <strong>montant_ht</strong>, montant_ttc, acompte_ttc, date_acompte, solde_ttc, date_solde, vendeur.
            </p>
            <Button variant="outline" size="sm" className="mt-2" onClick={downloadTemplate} data-testid="import-download-template">
              <Download className="h-3.5 w-3.5 mr-1" /> Télécharger un modèle Excel
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full sm:w-auto text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
              data-testid="import-file-input"
            />
            <Button onClick={uploadPreview} disabled={!file || uploading} data-testid="import-preview-btn">
              {uploading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
              Analyser
            </Button>
            {(preview || file) && (
              <Button variant="ghost" onClick={reset}>
                <RotateCcw className="h-4 w-4 mr-1" /> Réinitialiser
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Step 2: mapping */}
      {preview && (
        <Card data-testid="import-mapping">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">2. Mapper les types et vendeurs inconnus</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview.unknown_work_types?.length > 0 && (
              <div>
                <Label className="text-sm font-semibold">Types de travaux inconnus</Label>
                <div className="space-y-2 mt-2">
                  {preview.unknown_work_types.map((w) => (
                    <div key={w} className="flex items-center gap-2 flex-wrap" data-testid={`wt-row-${w}`}>
                      <span className="text-sm font-mono px-2 py-0.5 rounded bg-muted min-w-[160px]">{w}</span>
                      <span className="text-xs text-muted-foreground">→</span>
                      <Select value={workTypeMap[w] || "__create__"} onValueChange={(v) => setWorkTypeMap((m) => ({ ...m, [w]: v }))}>
                        <SelectTrigger className="w-[280px]" data-testid={`wt-select-${w}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__create__">✨ Créer « {w} »</SelectItem>
                          {preview.existing_work_types.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {preview.unknown_sellers?.length > 0 && (
              <div>
                <Label className="text-sm font-semibold">Vendeurs inconnus</Label>
                <div className="space-y-2 mt-2">
                  {preview.unknown_sellers.map((s) => (
                    <div key={s} className="flex items-center gap-2 flex-wrap" data-testid={`seller-row-${s}`}>
                      <span className="text-sm font-mono px-2 py-0.5 rounded bg-muted min-w-[160px]">{s}</span>
                      <span className="text-xs text-muted-foreground">→</span>
                      <Select value={sellerMap[s] || "__create__"} onValueChange={(v) => setSellerMap((m) => ({ ...m, [s]: v }))}>
                        <SelectTrigger className="w-[280px]" data-testid={`seller-select-${s}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__create__">✨ Créer « {s} »</SelectItem>
                          {preview.existing_sellers.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-md border border-primary/20">
              <Switch checked={autoCreateClients} onCheckedChange={setAutoCreateClients} id="auto-clients" data-testid="import-auto-clients" />
              <Label htmlFor="auto-clients" className="text-sm">
                Créer automatiquement les fiches clients manquantes
                {preview.new_clients_count > 0 && (
                  <Badge variant="outline" className="ml-2 text-xs">{preview.new_clients_count} nouveaux</Badge>
                )}
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: preview rows */}
      {preview && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 flex-wrap">
              3. Aperçu des lignes
              <Badge className="bg-emerald-100 text-emerald-800">{okCount} OK</Badge>
              {warnCount > 0 && <Badge className="bg-amber-100 text-amber-800">{warnCount} avertissements</Badge>}
              {errorCount > 0 && <Badge className="bg-red-100 text-red-800">{errorCount} erreurs</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="import-preview-table">
                <thead className="bg-muted/40">
                  <tr>
                    <th className="p-1.5 text-left">L.</th>
                    <th className="p-1.5 text-left">Statut</th>
                    <th className="p-1.5 text-left">Réf</th>
                    <th className="p-1.5 text-left">Client</th>
                    <th className="p-1.5 text-left">Ville</th>
                    <th className="p-1.5 text-left">Travaux</th>
                    <th className="p-1.5 text-right">HT</th>
                    <th className="p-1.5 text-right">TTC</th>
                    <th className="p-1.5 text-right">Acompte</th>
                    <th className="p-1.5 text-right">Solde</th>
                    <th className="p-1.5 text-left">Vendeur</th>
                    <th className="p-1.5 text-left">Nouv. client ?</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r, i) => {
                    const meta = STATUS_META[r.status] || STATUS_META.ok;
                    const Icon = meta.icon;
                    return (
                      <tr key={i} className={`border-b ${meta.bg} hover:brightness-95`} data-testid={`row-${r._line}`}>
                        <td className="p-1.5 font-mono">{r._line}</td>
                        <td className="p-1.5">
                          <span className={`inline-flex items-center gap-1 ${meta.color}`} title={[...(r.errors || []), ...(r.warnings || [])].join(" / ")}>
                            <Icon className="h-3 w-3" />
                          </span>
                        </td>
                        <td className="p-1.5 font-mono">{r.reference || "—"}</td>
                        <td className="p-1.5">{r.client_name || "—"}</td>
                        <td className="p-1.5">{r.city || "—"}</td>
                        <td className="p-1.5">{r.work_type_label || "—"}</td>
                        <td className="p-1.5 text-right">{formatMoney(r.amount_ht)}</td>
                        <td className="p-1.5 text-right">{formatMoney(r.amount_ttc)}</td>
                        <td className="p-1.5 text-right">{formatMoney(r.deposit_amount)}<div className="text-[10px] text-muted-foreground">{fmtDate(r.deposit_date)}</div></td>
                        <td className="p-1.5 text-right">{formatMoney(r.balance_amount)}<div className="text-[10px] text-muted-foreground">{fmtDate(r.balance_date)}</div></td>
                        <td className="p-1.5">{r.seller_name || "—"}</td>
                        <td className="p-1.5">{r.is_new_client ? <Badge variant="outline" className="text-[10px]">Nouveau</Badge> : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {errorCount > 0 && (
              <Alert variant="destructive" className="mt-3 bg-red-50 text-red-900 border-red-300">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {errorCount} ligne(s) avec erreur seront ignorées lors de l'import.
                </AlertDescription>
              </Alert>
            )}

            <div className="flex justify-end mt-3">
              <Button onClick={doImport} disabled={importing || importableCount === 0 || !!result} size="lg" data-testid="import-confirm-btn">
                {importing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}
                Importer {importableCount} commande(s)
              </Button>
            </div>

            {result && (
              <Alert className="mt-4 bg-emerald-50 border-emerald-200" data-testid="import-result">
                <CheckCircle2 className="h-4 w-4 text-emerald-700" />
                <AlertDescription className="text-emerald-900">
                  <strong>Import terminé.</strong> {result.created_orders} commandes, {result.created_payments} paiements, {result.created_clients} fiches clients créées.
                  <Button variant="outline" size="sm" className="ml-3" onClick={() => rollback(result.batch_id)} data-testid="import-rollback-btn">
                    <Undo2 className="h-3 w-3 mr-1" /> Annuler ce batch
                  </Button>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Batch history */}
      {batches.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Historique des imports</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2" data-testid="import-batches">
              {batches.map((b) => (
                <li key={b.id} className="border rounded p-2 text-sm flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-xs">{b.id.slice(0, 8)}</span>
                  <span className="text-muted-foreground">{fmtDate(b.created_at)} {new Date(b.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</span>
                  <Badge variant="outline">{b.counts?.orders || 0} cmd</Badge>
                  <Badge variant="outline">{b.counts?.payments || 0} pmt</Badge>
                  <Badge variant="outline">{b.counts?.clients || 0} clients</Badge>
                  <Button size="sm" variant="ghost" className="ml-auto text-red-600" onClick={() => rollback(b.id)} data-testid={`batch-rollback-${b.id}`}>
                    <Trash2 className="h-3 w-3 mr-1" /> Annuler
                  </Button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ImportOrdersPage;
